import { Add as AddIcon } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { AvailabilityEditor } from '../../lib/components/AvailabilityEditor';
import { PreferenceForm } from '../../lib/components/PreferenceForm';
import { TeacherCard } from '../../lib/components/TeacherCard';
import { TeacherForm } from '../../lib/components/TeacherForm';
import { TeacherTable } from '../../lib/components/TeacherTable';
import { generateFullTimeAvailability } from '../../lib/mockData/teachers';
import { useTeacherStore } from '../../stores/teacherStore';
import { Teacher, TeacherAvailability, TeacherPreferences } from '../../types';

type DialogMode = 'create' | 'edit' | 'view' | 'availability' | 'preferences' | null;

export const Teachers: React.FC = () => {
  const {
    teachers,
    addTeacher,
    updateTeacher,
    deleteTeacher,
    updateTeacherAvailability,
    updateTeacherPreferences,
    getTotalTeachers,
  } = useTeacherStore();

  const [dialogMode, setDialogMode] = useState<DialogMode>(null);
  const [selectedTeacher, setSelectedTeacher] = useState<Teacher | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const handleCreate = () => {
    setSelectedTeacher(null);
    setDialogMode('create');
  };

  const handleEdit = (teacher: Teacher) => {
    setSelectedTeacher(teacher);
    setDialogMode('edit');
  };

  const handleView = (teacher: Teacher) => {
    setSelectedTeacher(teacher);
    setDialogMode('view');
    setActiveTab(0);
  };

  const handleDelete = (teacher: Teacher) => {
    setSelectedTeacher(teacher);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = () => {
    if (selectedTeacher) {
      deleteTeacher(selectedTeacher.id);
      setDeleteConfirmOpen(false);
      setSelectedTeacher(null);
      if (dialogMode === 'view') {
        setDialogMode(null);
      }
    }
  };

  const handleFormSubmit = (data: any) => {
    if (dialogMode === 'create') {
      const newTeacher: Teacher = {
        id: `teacher-${Date.now()}`,
        ...data,
        availability: generateFullTimeAvailability(),
        preferences: {
          preferredDays: [],
          avoidedDays: [],
          preferredPeriods: [],
          avoidedPeriods: [],
          preferredRooms: [],
        },
      };
      addTeacher(newTeacher);
    } else if (dialogMode === 'edit' && selectedTeacher) {
      updateTeacher(selectedTeacher.id, data);
    }
    setDialogMode(null);
    setSelectedTeacher(null);
  };

  const handleAvailabilityChange = (availability: TeacherAvailability) => {
    if (selectedTeacher) {
      updateTeacherAvailability(selectedTeacher.id, availability);
      // Update local state to reflect changes
      setSelectedTeacher({ ...selectedTeacher, availability });
    }
  };

  const handlePreferencesSubmit = (preferences: TeacherPreferences) => {
    if (selectedTeacher) {
      updateTeacherPreferences(selectedTeacher.id, preferences);
      // Update local state
      setSelectedTeacher({ ...selectedTeacher, preferences });
      setDialogMode('view');
    }
  };

  const handleEditFromView = () => {
    if (selectedTeacher) {
      setDialogMode('edit');
    }
  };

  const handleEditAvailability = () => {
    if (selectedTeacher) {
      setDialogMode('availability');
    }
  };

  const handleEditPreferences = () => {
    if (selectedTeacher) {
      setDialogMode('preferences');
    }
  };

  const getDialogTitle = () => {
    switch (dialogMode) {
      case 'create':
        return 'Add New Teacher';
      case 'edit':
        return 'Edit Teacher';
      case 'view':
        return 'Teacher Details';
      case 'availability':
        return 'Edit Availability';
      case 'preferences':
        return 'Edit Preferences';
      default:
        return '';
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Teachers
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage teacher information, availability, and scheduling preferences
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>
          Add Teacher
        </Button>
      </Box>

      {/* Summary Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Total Teachers
            </Typography>
            <Typography variant="h4">{getTotalTeachers()}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Full-time
            </Typography>
            <Typography variant="h4">{teachers.filter((t) => t.contractType === 'full-time').length}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Part-time
            </Typography>
            <Typography variant="h4">{teachers.filter((t) => t.contractType === 'part-time').length}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Supply
            </Typography>
            <Typography variant="h4">{teachers.filter((t) => t.contractType === 'supply').length}</Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Teachers Table */}
      <Paper sx={{ p: 2 }}>
        <TeacherTable teachers={teachers} onEdit={handleEdit} onDelete={handleDelete} onRowClick={handleView} />
      </Paper>

      {/* Create/Edit Dialog */}
      <Dialog
        open={dialogMode === 'create' || dialogMode === 'edit'}
        onClose={() => setDialogMode(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{getDialogTitle()}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TeacherForm
              teacher={selectedTeacher || undefined}
              onSubmit={handleFormSubmit}
              onCancel={() => setDialogMode(null)}
            />
          </Box>
        </DialogContent>
      </Dialog>

      {/* View Dialog with Tabs */}
      <Dialog open={dialogMode === 'view'} onClose={() => setDialogMode(null)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {getDialogTitle()}
            <Button variant="outlined" size="small" onClick={handleEditFromView}>
              Edit
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
              <Tab label="Details" />
              <Tab label="Availability" />
              <Tab label="Preferences" />
            </Tabs>
          </Box>

          {selectedTeacher && (
            <>
              {activeTab === 0 && <TeacherCard teacher={selectedTeacher} showAvailability={false} />}

              {activeTab === 1 && (
                <Box>
                  <TeacherCard teacher={selectedTeacher} showAvailability={true} />
                  <Box sx={{ mt: 2, textAlign: 'right' }}>
                    <Button variant="outlined" onClick={handleEditAvailability}>
                      Edit Availability
                    </Button>
                  </Box>
                </Box>
              )}

              {activeTab === 2 && (
                <Box>
                  <TeacherCard teacher={selectedTeacher} showPreferences={true} />
                  <Box sx={{ mt: 2, textAlign: 'right' }}>
                    <Button variant="outlined" onClick={handleEditPreferences}>
                      Edit Preferences
                    </Button>
                  </Box>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogMode(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Availability Editor Dialog */}
      <Dialog open={dialogMode === 'availability'} onClose={() => setDialogMode('view')} maxWidth="md" fullWidth>
        <DialogTitle>{getDialogTitle()}</DialogTitle>
        <DialogContent>
          {selectedTeacher && (
            <Box sx={{ pt: 2 }}>
              <AvailabilityEditor availability={selectedTeacher.availability} onChange={handleAvailabilityChange} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogMode('view')}>Done</Button>
        </DialogActions>
      </Dialog>

      {/* Preferences Editor Dialog */}
      <Dialog open={dialogMode === 'preferences'} onClose={() => setDialogMode('view')} maxWidth="md" fullWidth>
        <DialogTitle>{getDialogTitle()}</DialogTitle>
        <DialogContent>
          {selectedTeacher && (
            <Box sx={{ pt: 2 }}>
              <PreferenceForm
                preferences={selectedTeacher.preferences}
                onSubmit={handlePreferencesSubmit}
                onCancel={() => setDialogMode('view')}
              />
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} maxWidth="xs">
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This will remove the teacher from all staff role assignments.
          </Alert>
          <Typography>
            Are you sure you want to delete{' '}
            <strong>
              {selectedTeacher?.firstName} {selectedTeacher?.lastName}
            </strong>
            ?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Teachers;
